import _     from 'underscore';
import React from 'react';

import { Align, SemanticUIColor, Size, Uuid }                                                                                                                                                     from "@src/types";
import { ENTITY_ICONS }                                                                                                                                                                           from "@src/constants/ProductEnums";
import { MenuizedFocusContainer, MenuizedFocusContainerViewTypeConfig, MenuizedFocusedViewValue }                                                                                                 from "@src/views/containers/MenuizedFocusContainer";
import { BasicDialogContainer }                                                                                                                                                                   from "@src/views/components/modals/BasicDialogContainer";
import { BasicDialog }                                                                                                                                                                            from "@src/views/components/modals/BasicDialog";
import { BulkUpdateAttributeInfo }                                                                                                                                                                from "@src/views/pages/projects/activities/bulk/BulkUpdateAttributeInfo";
import { ProjectActivityBulkUpdateHireType }                                                                                                                                                      from "@src/views/pages/projects/activities/bulk/slices/ProjectActivityBulkUpdateHireType";
import { ProjectActivityBulkUpdateNotes }                                                                                                                                                         from "@src/views/pages/projects/activities/bulk/slices/ProjectActivityBulkUpdateNotes";
import { ProjectActivityBulkUpdateMenuizedFocusFormContainer, ProjectActivityBulkUpdateMenuizedFocusFormContainerProps }                                                                          from "@src/views/pages/projects/activities/bulk/ProjectActivityBulkUpdateMenuizedFocusFormContainer";
import { ProjectActivityBulkUpdateOperationalCost }                                                                                                                                               from "@src/views/pages/projects/activities/bulk/slices/ProjectActivityBulkUpdateOperationalCost";
import { ProjectActivityBulkUpdateOngoingCost }                                                                                                                                                   from "@src/views/pages/projects/activities/bulk/slices/ProjectActivityBulkUpdateOngoingCost";
import { StaticProjectActivityBulkUpdateTiming }                                                                                                                                                 from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateTiming";
import { corvalManagedProjectActivityBulkUpdateTypes, ProjectActivityBulkUpdateType, projectActivityBulkUpdateTypes, projectActivityBulkUpdateTypeTitles, staticProjectActivityBulkUpdateTypes } from "@src/views/pages/projects/operations/ProjectActivityBulkUpdateDialog";
import { StaticProjectActivityBulkUpdateHireType }                                                                                                                                               from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateHireType";
import { StaticProjectActivityBulkUpdateOperationalCost }                                                                                                                                         from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateOperationalCost";
import { StaticProjectActivityBulkUpdateOngoingCost }                                                                                                                                             from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateOngoingCost";
import { StaticProjectActivityBulkUpdateCriticality }                                                                                                                                             from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateCriticality";
import { StaticProjectActivityBulkUpdateBusinessFunction }                                                                                                                                        from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateBusinessFunction";
import { StaticProjectActivityBulkUpdateCompletionState }                                                                                                                                         from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateCompletionState";
import { StaticProjectActivityBulkUpdateIsAtRisk }                                                                                                                                                from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateIsAtRisk";


interface ProjectActivityBulkUpdateMultiDialogProps extends Pick<ProjectActivityBulkUpdateMenuizedFocusFormContainerProps, 'onUpdateComplete' | 'onUpdateError'> {
    title?: string,
    projectId?: null,
    projectVersionId?: null,
    projectActivityIds: Uuid[],
    showOptionsForTypes?: Array<ProjectActivityBulkUpdateType> | boolean,
}

const ProjectActivityBulkUpdateMultiDialogDefaultProps = {
    title:               'Bulk Update Activities',
    onUpdateComplete:    null,
    onUpdateError:       null,
    showOptionsForTypes: true,
} satisfies Partial<ProjectActivityBulkUpdateMultiDialogProps>

function ProjectActivityBulkUpdateMultiDialog( _props: ProjectActivityBulkUpdateMultiDialogProps ){

    const props = { ...ProjectActivityBulkUpdateMultiDialogDefaultProps, ..._props };

    // Indicate if we're using the Corval managed areas
    // for (Project Activities), or not (Static Project Activities)
    const corvalManaged = _.isEmpty( props.projectVersionId );

    const validUpdateTypes: Array<ProjectActivityBulkUpdateType> =
              corvalManaged
              ? corvalManagedProjectActivityBulkUpdateTypes
              : staticProjectActivityBulkUpdateTypes;

    const bulkUpdaterProps = {
        onUpdateComplete: props.onUpdateComplete,
        onUpdateError:    props.onUpdateError,
    }

    const showOptionsForType = ( type: ProjectActivityBulkUpdateType ) => {
        if( _.isArray( props.showOptionsForTypes ) ){
            return props.showOptionsForTypes.includes( type );
        }
        return props.showOptionsForTypes ?? true;
    }

    const getOptionProps = ( type: ProjectActivityBulkUpdateType ) => {
        if( !showOptionsForType( type ) ){
            return {
                maxOptionWidth: showOptionsForType( type ) ? null : '0px',
                renderOptions:  null
            }
        }

        return {};
    }

    return (
        <BasicDialog
            showSubmitButton={ false }
            controlAlign={ Align.CENTER }
            className={ 'no-padding' }
            cancelLabel={ 'Close' }>

            <BasicDialogContainer title={ props.title } titleColor={ SemanticUIColor.PURPLE } contentPadding={ Size.MD }>

                <MenuizedFocusContainer<ProjectActivityBulkUpdateType>
                    layoutProps={ {
                        minContainerHeight: '500px',
                    } }
                    initialViewType={
                        ( !corvalManaged )
                        ? projectActivityBulkUpdateTypes.timing
                        : projectActivityBulkUpdateTypes.hireType }
                    viewsTypes={ _.pick( {

                        [ projectActivityBulkUpdateTypes.timing ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.timing,
                            menuIcon:      ENTITY_ICONS.ACTIVITIES.TIMING.NORMAL,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateTiming
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <BulkUpdateAttributeInfo
                                    title={ 'About Timing' }
                                    description={ 'Updating the timing for activities will change when they start or end, and possibly update dependant activities.' }
                                />
                            ),
                            ...getOptionProps( projectActivityBulkUpdateTypes.timing )
                        },

                        [ projectActivityBulkUpdateTypes.isAtRisk ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.isAtRisk,
                            menuIcon:      ENTITY_ICONS.ACTIVITIES.RISK_RATING,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateIsAtRisk
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            ...getOptionProps( projectActivityBulkUpdateTypes.criticality )
                        },

                        [ projectActivityBulkUpdateTypes.criticality ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.criticality,
                            menuIcon:      ENTITY_ICONS.ACTIVITIES.CRITICALITY_RATING,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateCriticality
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            ...getOptionProps( projectActivityBulkUpdateTypes.criticality )
                        },

                        [ projectActivityBulkUpdateTypes.businessFunction ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.businessFunction,
                            menuIcon:      ENTITY_ICONS.BUSINESS_FUNCTIONS.ENTITY,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateBusinessFunction
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            ...getOptionProps( projectActivityBulkUpdateTypes.businessFunction )
                        },

                        [ projectActivityBulkUpdateTypes.completionState ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.completionState,
                            menuIcon:      ENTITY_ICONS.ACTIVITIES.STATUS,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateCompletionState
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            ...getOptionProps( projectActivityBulkUpdateTypes.completionState )
                        },

                        [ projectActivityBulkUpdateTypes.hireType ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.hireType,
                            menuIcon:      ENTITY_ICONS.RESOURCING.HIRES.ENTITY,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                ( !corvalManaged )
                                ? <StaticProjectActivityBulkUpdateHireType
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                                : <ProjectActivityBulkUpdateHireType
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <BulkUpdateAttributeInfo
                                    title={ 'About Hire Types' }
                                    description={ 'Updating the hire types for activities will transition them to a new resource, which may update your hiring budget calculations.' }
                                />
                            ),
                            ...getOptionProps( projectActivityBulkUpdateTypes.hireType )
                        },

                        [ projectActivityBulkUpdateTypes.operationalCost ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.operationalCostOverride,
                            menuIcon:      ENTITY_ICONS.BUDGET.OPERATIONAL_COSTS,
                            exclude:       _.isEmpty( props.projectVersionId ),
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateOperationalCost
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: null,
                            ...getOptionProps( projectActivityBulkUpdateTypes.operationalCost )
                        },

                        [ projectActivityBulkUpdateTypes.operationalCostOverride ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.operationalCost,
                            menuIcon:      ENTITY_ICONS.BUDGET.OPERATIONAL_COSTS,
                            exclude:       !_.isEmpty( props.projectVersionId ),
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <ProjectActivityBulkUpdateOperationalCost
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <BulkUpdateAttributeInfo
                                    title={ 'About Operational Costs' }
                                    description={ 'Updating the operational budget values for activities will allow specific adjustments to the initial costs. This may override built-in professional fees Corval calculates for, but allows for more accurate budgetary planning.' }
                                />
                            ),
                            ...getOptionProps( projectActivityBulkUpdateTypes.operationalCostOverride )
                        },

                        [ projectActivityBulkUpdateTypes.ongoingCost ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.ongoingCost,
                            menuIcon:      ENTITY_ICONS.BUDGET.ONGOING_COSTS,
                            exclude:       _.isEmpty( props.projectVersionId ),
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateOngoingCost
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: null,
                            ...getOptionProps( projectActivityBulkUpdateTypes.ongoingCost )
                        },

                        [ projectActivityBulkUpdateTypes.ongoingCostOverride ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.ongoingCostOverride,
                            menuIcon:      ENTITY_ICONS.BUDGET.ONGOING_COSTS,
                            exclude:       !_.isEmpty( props.projectVersionId ),
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <ProjectActivityBulkUpdateOngoingCost
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <BulkUpdateAttributeInfo
                                    title={ 'About Ongoing Costs' }
                                    description={ 'Updating the ongoing budget values for activities will allow specific adjustments to the costs incurred over time, which may allow for more accurate budgetary planning' }
                                />
                            ),
                            ...getOptionProps( projectActivityBulkUpdateTypes.ongoingCostOverride )
                        },

                        [ projectActivityBulkUpdateTypes.notes ]: {
                            menuLabel:     projectActivityBulkUpdateTypeTitles.notes,
                            menuIcon:      'pencil',
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <ProjectActivityBulkUpdateNotes
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <ProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <BulkUpdateAttributeInfo
                                    title={ 'About Notes' }
                                    description={ 'Updating the notes for activities will allow anyone reviewing information about the selected items to see additional details.' }
                                />
                            ),
                            ...getOptionProps( projectActivityBulkUpdateTypes.notes )
                        },

                    }, [ validUpdateTypes ] ) as Record<ProjectActivityBulkUpdateType, MenuizedFocusContainerViewTypeConfig<ProjectActivityBulkUpdateType>> }
                />

            </BasicDialogContainer>

        </BasicDialog>
    );
}

export {
    ProjectActivityBulkUpdateMultiDialog,
    ProjectActivityBulkUpdateMultiDialogDefaultProps,
    type ProjectActivityBulkUpdateMultiDialogProps,
};
